import React from 'react';
import { Link } from 'react-router-dom';
import { PiLaptopLight, PiCalendarCheckLight, PiListChecksLight } from 'react-icons/pi';
import { programmes } from '../data/programmes';
import Cta from '../components/Cta';
import Reveal from '../components/Reveal';

interface Story {
  name: string;
  image: string;
  /** A graduate's own words, rendered as a pull quote. */
  quote?: string;
  /** Editorial caption, for students without a written testimony. */
  caption?: string;
  location?: string;
}

const stories: Story[] = [
  {
    name: 'Tendai Mzanywa',
    image: '/img/success/child.jpeg',
    quote:
      'Leolilian Care International helped me develop essential skills for caring for children of various ages, including safety protocols, developmental activities, and positive discipline techniques. The training I received made me confident and prepared to start my pre-school.',
  },
  {
    name: 'Adam Chanaiwa',
    image: '/img/success/Adam.jpeg',
    quote:
      'Leolilian Care International provided me with comprehensive training in elderly care giving. I learned essential skills for supporting seniors, including health monitoring, mobility assistance, medication management, and creating engaging activities. The practical experience I gained has made me confident and well-prepared for my career.',
  },
  {
    name: 'Margaret Moyo',
    // The filename contains a space, so it must be percent-encoded here —
    // an unescaped space breaks the request on some static hosts.
    image: '/img/success/Margaret%20Moyo.jpeg',
    caption: 'Our student doing her practical in Masvingo, Zimbabwe.',
    location: 'Masvingo, Zimbabwe',
  },
];

const HomePage: React.FC = () => {
  return (
    <>
      {/* ---------------------------------------------------------------- HERO
          Editorial Split: massive type left, the programme island right. */}
      <section
        id="home"
        className="relative min-h-[100dvh] overflow-hidden bg-mesh-warm px-4 pb-24 pt-36 sm:px-8 sm:pt-44 lg:px-12"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="eyebrow bg-plum-pale text-plum-deep">
                <span className="h-1 w-1 rounded-full bg-plum" />
                Est. 2024 · Germiston, South Africa
              </span>
            </Reveal>

            <Reveal delay={80}>
              <h1 className="mt-7 font-display text-[3.25rem] font-light leading-[0.95] tracking-[-0.035em] text-espresso-900 sm:text-7xl lg:text-8xl">
                Legacy Building
                <br />
                <span className="italic text-sage-deep">Through</span> Caregiving
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-espresso-600 sm:text-base">
                Accessible, flexible and career-focused foundation training — delivered 100%
                online across three specialised programmes.
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Cta to="/enroll">Enroll Now</Cta>
                <Cta href="https://wa.me/27672594463" variant="ghost" external>
                  Chat on WhatsApp
                </Cta>
              </div>
            </Reveal>
          </div>

          {/* Programme island */}
          <Reveal delay={320} className="lg:col-span-5">
            <div className="bezel-shell-lg shadow-lifted">
              <div className="bezel-core-lg overflow-hidden p-8 sm:p-10">
                <p className="text-[10px] uppercase tracking-eyebrow text-espresso-500">
                  Three programmes
                </p>

                <ul className="mt-6 space-y-1">
                  {programmes.map(({ icon: Icon, shortTitle, duration, modules }) => (
                    <li
                      key={shortTitle}
                      className="group flex items-center gap-4 rounded-2xl px-3 py-3.5 transition-colors duration-700 ease-fluid hover:bg-espresso-700/[0.04]"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sage-pale text-sage-deep transition-transform duration-700 ease-fluid group-hover:scale-105">
                        <Icon className="h-4 w-4" strokeWidth={1} />
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-espresso-800">
                          {shortTitle}
                        </span>
                        <span className="block text-xs text-espresso-500">
                          {modules.length} modules
                        </span>
                      </span>
                      <span className="text-xs text-espresso-500">{duration}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 space-y-3 border-t border-espresso-700/10 pt-6">
                  <p className="flex items-start gap-3 text-xs leading-relaxed text-espresso-600">
                    <PiLaptopLight className="mt-px h-4 w-4 shrink-0" strokeWidth={1} />
                    Delivered 100% online
                  </p>
                  <p className="flex items-start gap-3 text-xs leading-relaxed text-espresso-600">
                    <PiCalendarCheckLight className="mt-px h-4 w-4 shrink-0" strokeWidth={1} />
                    Plus 5 additional days for assignment completion and submission
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ----------------------------------------------------------- PROGRAMMES */}
      <section id="courses" className="px-4 py-24 sm:px-8 sm:py-32 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="flex flex-col justify-between gap-8 border-t border-espresso-700/10 pt-12 md:flex-row md:items-end">
              <div>
                <span className="eyebrow bg-sage-pale text-sage-deep">Programmes</span>
                <h2 className="mt-6 max-w-2xl font-display text-4xl font-light leading-[1.05] tracking-tight text-espresso-900 sm:text-6xl">
                  Three specialised
                  <br />
                  online programmes.
                </h2>
              </div>
              <Cta to="/courses" variant="ghost">
                See all programmes
              </Cta>
            </div>
          </Reveal>

          {/* Asymmetric bento — collapses to a single column below md */}
          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12">
            {programmes.map(({ icon: Icon, title, duration, hours, summary, modules }, i) => (
              <Reveal
                key={title}
                delay={i * 90}
                className={i === 0 ? 'md:col-span-7' : i === 1 ? 'md:col-span-5' : 'md:col-span-12'}
              >
                <Link to="/courses" className="group block h-full">
                  <div className="bezel-shell h-full transition-all duration-700 ease-fluid hover:-translate-y-1 hover:shadow-lifted">
                    <div className="bezel-core flex h-full flex-col p-7 sm:p-8">
                      <div className="flex items-start justify-between gap-4">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-espresso-700/[0.05] text-espresso-700 transition-transform duration-700 ease-fluid group-hover:scale-105">
                          <Icon className="h-5 w-5" strokeWidth={1} />
                        </span>
                        <span className="eyebrow bg-espresso-700/[0.05] text-espresso-600">
                          {duration}
                          {hours ? ` · ${hours}` : ''}
                        </span>
                      </div>
                      <div className="mt-10">
                        <h3 className="font-display text-2xl font-light tracking-tight text-espresso-900">
                          {title}
                        </h3>
                        <p className="mt-3 max-w-xl text-sm leading-relaxed text-espresso-600">
                          {summary}
                        </p>
                        <p className="mt-5 inline-flex items-center gap-2 text-xs text-espresso-500">
                          <PiListChecksLight className="h-4 w-4" strokeWidth={1} />
                          {modules.length} modules
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ SUCCESS STORIES */}
      <section className="px-4 py-24 sm:px-8 sm:py-32 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <span className="eyebrow bg-plum-pale text-plum-deep">Success stories</span>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tight text-espresso-900 sm:text-6xl">
              Graduates who went on to build something of their own.
            </h2>
          </Reveal>

          {/* Two testimonies side by side, then a full-width editorial card for
              the student on placement — her caption is far shorter than a quote,
              so a horizontal layout carries it better than a third tall column. */}
          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
            {stories.map((story, i) => {
              const wide = !story.quote;
              return (
                <Reveal
                  key={story.name}
                  delay={i * 120}
                  className={wide ? 'md:col-span-12' : 'md:col-span-6'}
                >
                  <figure className="bezel-shell-lg group h-full">
                    <div
                      className={`bezel-core-lg flex h-full overflow-hidden ${
                        wide ? 'flex-col lg:flex-row' : 'flex-col'
                      }`}
                    >
                      <div
                        className={
                          wide
                            ? 'overflow-hidden rounded-[calc(2.5rem-0.5rem)] rounded-b-none lg:w-5/12 lg:rounded-r-none lg:rounded-bl-[calc(2.5rem-0.5rem)]'
                            : 'overflow-hidden rounded-[calc(2.5rem-0.5rem)] rounded-b-none'
                        }
                      >
                        <img
                          src={story.image}
                          alt={story.name}
                          loading="lazy"
                          className={`w-full object-cover transition-transform duration-1200 ease-fluid group-hover:scale-[1.04] ${
                            wide ? 'h-72 lg:h-full lg:min-h-[22rem]' : 'h-72 sm:h-80'
                          }`}
                        />
                      </div>

                      <figcaption
                        className={`flex flex-1 flex-col p-8 sm:p-10 ${
                          wide ? 'lg:justify-center' : ''
                        }`}
                      >
                        {story.quote ? (
                          <blockquote className="flex-1 font-display text-lg font-light italic leading-relaxed text-espresso-700">
                            “{story.quote}”
                          </blockquote>
                        ) : (
                          <>
                            {story.location && (
                              <span className="eyebrow w-max bg-sage-pale text-sage-deep">
                                Practical placement
                              </span>
                            )}
                            <p className="mt-6 font-display text-2xl font-light leading-snug tracking-tight text-espresso-800 sm:text-3xl">
                              {story.caption}
                            </p>
                          </>
                        )}
                        <p className="mt-8 text-[10px] uppercase tracking-eyebrow text-espresso-500">
                          {story.name}
                          {story.location ? ` · ${story.location}` : ''}
                        </p>
                      </figcaption>
                    </div>
                  </figure>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------- ABOUT */}
      <section className="px-4 pb-32 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="bezel-shell-lg">
              <div className="bezel-core-lg relative overflow-hidden bg-espresso-800 px-8 py-20 text-cream sm:px-16 sm:py-28">
                <div className="relative z-10 mx-auto max-w-3xl text-center">
                  <span className="eyebrow bg-white/10 text-cream-200">Our story</span>
                  <h2 className="mt-8 font-display text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl">
                    Founded in 2024 by the children of Leo and Lilly.
                  </h2>
                  <p className="mx-auto mt-8 max-w-2xl text-[15px] leading-relaxed text-cream-200/80">
                    We aim to build a legacy of caregiving through our specialised training
                    programmes, providing essential skills for those looking to build a career
                    in caregiving, hospitality and professional cookery.
                  </p>
                  <div className="mt-12 flex justify-center">
                    <Cta to="/about" variant="light">
                      Read our story
                    </Cta>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default HomePage;
